import { ClockCircleFilled, ClockCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { Avatar, Button, Dropdown, MenuProps, Tag, Tooltip } from 'antd';
import cn from 'classnames';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import systemPages from 'helpers/pagesAndLocation';
import { IDataRoom } from 'interfaces';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useAppSelector } from 'store/hook';
import classes from './RoomsTab.module.scss';
import Assets from 'assets/icons';
import { Tariffs } from 'components/TariffsComponent/tariffsData';
import api from 'api';

type RoomCardProps = {
  room: IDataRoom;
  onOpenClick: (_: any, room: IDataRoom, isLoadingRoom: boolean, option?: string) => Promise<void>;
  controlItems: (room: IDataRoom, isLoading: boolean, canEditRoom: boolean) => MenuProps['items'];
  style: any;
  searchValue?: string;
  markText?: (text: string, markerRef: React.RefObject<HTMLDivElement>, timeout?: number) => void;
  isNormalSideBar?: boolean;
};

const RoomCard = ({ room, onOpenClick, controlItems, style, searchValue, markText, isNormalSideBar }: RoomCardProps) => {  
  const [needLoadPermissions, setNeedLoadPermissions] = React.useState(true);
  const [canEditSettings, setCanEditSettings] = React.useState(false);

  const { isLoadingUserPermissions, userData } = useAppSelector((store) => store.userData);
  const { isLoadingRoom, dataRoom } = useAppSelector((store) => store.dataRoom);
  const { selectedKeys } = useAppSelector((store) => store.windowState);  

  const { name, last_action_timestamp, agreement_enable, created_at, invited_at } = room;
  const { t } = useTranslation();
  const navigate = useNavigate();
    
  const dateFormat = userData?.date_format || 'international'
  const isLoadingSomeRoom = isLoadingRoom || isLoadingUserPermissions;
  const params = useParams();
  const markerRef = React.useRef<HTMLDivElement>(null);
  const isCurrentRoom = params.room === room.id;
  const activeUser = !room.is_blocked;

  const isNewRoom = room.system_status === 'new';
  const isExpiredRoom = room.system_status === 'expired';
  const needCompletePayment = room.tariff?.status?.toLowerCase() === 'awaiting_payment' || (room?.tariff?.tariff_name && room.tariff?.tariff_ends_at === null);
  const unavailableRoom = isExpiredRoom || needCompletePayment;

  React.useEffect(() => {
    if (markText) {
      const text = searchValue || '';
      markText(text, markerRef);
    }
  }, [searchValue]);

  const fetchPermissions = async (open: boolean) => {
    if (open && needLoadPermissions) {
      const permissionsOfThisRoom = await api.getRoomsPermissions({room_id: room.id})
      setCanEditSettings(permissionsOfThisRoom.data?.can_edit_room_settings);
      setNeedLoadPermissions(false);
    }
  };
  
  const preventDef = (e: React.MouseEvent<any, MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const navigateToNDASection = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    preventDef(e);    
    if (unavailableRoom) {
      return;
    } else {
      localStorage.setItem('scrollToRelatedRooms', 'true');
      if (dataRoom?.id === room.id) {
        navigate(`/room/${room?.id}/nda`);
      } else {
        onOpenClick(null, room, isLoadingSomeRoom, 'nda');
      }
    }
  }

  return (
    <div
      className={cn(
        classes.room,
        isCurrentRoom ? classes.activeRoom : 'null',
        systemPages.includes(selectedKeys[0]) ? classes.cursor : 'null',
        isLoadingSomeRoom ? classes.loadingRoom : 'null',
      )}
      style={style}
      id={`room_card_${room.id}`}
      onClick={(e) => onOpenClick(e, room, isLoadingSomeRoom)}
    >
      <div className={classes.roomContent}>

        <div className={classes.jusctifySpace}>

          <div
            ref={markerRef}
            className={cn(classes.roomTitle, isNormalSideBar ? classes.responsible : 'null')}
          >
            <div
              // onClick={preventDef}
              className={classes.avatarWrap}
            >
              {isNormalSideBar
              ? <Avatar className={classes.avatar} icon={<span className={classes.avatarWord}>{name[0]}</span>}/>
              : <Tooltip title={room.name} placement='right'>
                <Avatar className={classes.avatar} icon={<span className={classes.avatarWord}>{name[0]}</span>}/>
              </Tooltip>}

              {agreement_enable && <Tooltip align={{offset: [-8,-5]}} placement='topLeft' title={t('Rooms.NDARoom')}>
                <div>
                  <Assets.ShieldIcon className={classes.shield} />
                </div>
              </Tooltip>}

              {room.has_childs && <Tooltip align={{offset: [-11,-5]}} placement='topLeft' title={t('Rooms.hasRelatedRooms')}>
                <LinkOutlined
                  onClick={navigateToNDASection}
                  className={classes.chain}
                />
              </Tooltip>}
            
              {room?.access_terms_status === 'waiting_for_approve' &&
                (<Tooltip align={{offset: [-13,-5]}} placement='topLeft' title={t('Rooms.notAccessTerms')}>
                  <ClockCircleFilled className={classes.iconClock}/>
                </Tooltip>)
              }
            </div>
            <span className={classes.nameOverflow}>
              {isNewRoom && <Tag className={classes.tagNew} color='green'>{t('Rooms.new')}</Tag>}

              {room?.invite_id && <Tag className={classes.tagNew} color='processing'>{t('Rooms.invitation')}</Tag>}
              
              {needCompletePayment && <Tag className={classes.tagNew} color='gold'>{t('Rooms.notPaid')}</Tag>}
                
              {isExpiredRoom && (
                  room.tariff?.tariff_name === Tariffs.Trial
                    ? <Tooltip title={t('Rooms.expiredTrial')}><ClockCircleOutlined className={classes.tagExpired} /></Tooltip>
                    : <Tooltip title={t('Rooms.expiredTariff')}><ClockCircleOutlined className={classes.tagExpired} /></Tooltip>
                )
              }

              <Tooltip placement='topLeft' mouseEnterDelay={1} title={name}>
                {name}
              </Tooltip>
            </span>

          </div>

          {isNormalSideBar && <Dropdown
            menu={{ items: controlItems(room, needLoadPermissions, canEditSettings) }}
            onOpenChange={fetchPermissions}
            placement='topLeft'
            disabled={room.is_blocked}
            classNames={{ root: classes.controlMenu }}
          >
            <Button
              size='small'
              onClick={(e) => e.stopPropagation()}
              className={classes.iconBtn}
              icon={<Assets.VerticalIcon /> }
            />
          </Dropdown>}
        </div>

        <div className={classes.jusctifySpace}>
          <div className={classes.tagWrap}>
            {userData?.id === room.owner_id ?
              <Tooltip title={t('Rooms.badges.owner')} mouseEnterDelay={.3}>
                <Tag variant='outlined' className={classes.tagStatus}>{t('Rooms.badges.own')}</Tag>
              </Tooltip>
              : (room.is_admin && <Tooltip title={t('Rooms.badges.admin')} mouseEnterDelay={.3}>
                <Tag variant='outlined' className={classes.tagStatus}>{t('Rooms.badges.adm')}</Tag>
              </Tooltip>)
            }
          </div>
          
          <div className={classes.roomDescription}>

            {Boolean(room.documents_amount) && activeUser && (
              <Tag className={classes.tagInfo}>
                <Tooltip title={t('Rooms.badges.files')}>
                  <>
                    <Assets.FileIcon />
                    <span>{room?.documents_amount}</span>
                  </>
                </Tooltip>
                {Boolean(room?.new_count) && activeUser && (
                  <Tooltip title={t('Rooms.badges.new_files')}>
                    {<span className={classes.tagInfoNew}>+{room?.new_count}</span>}
                  </Tooltip>
                )}
              </Tag>
            )}

            {Boolean(room?.rooms_constraints?.members_amount) && activeUser && (
              <Tag className={cn(classes.tagInfo, classes.membersIcon)}>
                <Tooltip title={t('Rooms.badges.members')}>
                  <>
                    <Assets.MembersIcon />
                    {room?.rooms_constraints?.members_amount}
                  </>
                </Tooltip>
              </Tag>
            )}

            {Boolean(room.unviewed_amount) && (Number(room?.unviewed_amount) > 0) && activeUser && (
              <Tag className={cn(classes.tagInfo)}>
                <Tooltip title={t('Rooms.badges.new_messages')}>
                  <>
                    <Assets.ChatIcon />
                    {<span className={classes.tagInfoNew}>+{room.unviewed_amount}</span>}
                  </>
                </Tooltip>
              </Tag>
            )}
            
            <span>{
              last_action_timestamp || created_at 
                ? `${t('Rooms.lastUpd')} ${getDateWithTimeZone(last_action_timestamp || created_at, dateFormat)}`
                : invited_at
                  ? `${t('Rooms.invitedAt')} ${getDateWithTimeZone(invited_at, dateFormat)}`
                  : '-'
            }</span>

          </div>

        </div>

      </div>
    </div>
  );
};

export default RoomCard;
